import React from 'react'

import styled from 'styled-components'

import { focusOutline } from 'styles/shared'

type Props = {
  onClick?: (event: React.MouseEvent | React.KeyboardEvent) => void | null
  children: React.ReactNode
  className?: string
  readOnly?: boolean
  enabled?: boolean | string
}

const KeyboardAccessible = React.forwardRef<HTMLDivElement, Props>(({ children, onClick, className, readOnly, enabled = true, ...rest }, ref) => {
  const isEnabled = typeof enabled === 'string' ? enabled.toLowerCase() === 'true' : enabled

  const [showFocusOutline, setShowFocusOutline] = React.useState(isEnabled)

  const executeOnSpaceOrReturn = (event: React.KeyboardEvent) => {
    if ((event.key === 'Enter' || event.key === ' ') && onClick) {
      onClick(event)

      event.preventDefault()
    }
  }

  return (
    <S.Container
      ref={ref}
      className={className}
      role="button"
      tabIndex={isEnabled ? 0 : -1}
      onClick={onClick}
      readOnly={readOnly}
      isEnabled
      onKeyDown={executeOnSpaceOrReturn}
      // If the user is changing focus with the mouse we don't want to use the focus outline.
      onMouseDown={() => setShowFocusOutline(false)}
      onBlur={() => setShowFocusOutline(false)}
      // If the user is changing focus with the keyboard, we do want the focus outline.
      // It's important that this is onKeyUp because when tabbing to an element, the onKeyDown event won't get called.
      onKeyUp={() => setShowFocusOutline(true)}
      showFocusOutline={showFocusOutline}
      {...rest}
    >
      {children}
    </S.Container>
  )
})

export default KeyboardAccessible

const S = {
  Container: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isEnabled', 'showFocusOutline', 'fullWidth', 'textColor', 'indicator'].includes(prop)
  })<{ readOnly?: boolean, showFocusOutline: boolean, isEnabled: boolean }>`
    display: inline-block;
    ${({ readOnly }) => !readOnly ? 'cursor: pointer;' : ''}
    ${({ readOnly, showFocusOutline, isEnabled }) =>
    isEnabled && showFocusOutline
      ? focusOutline({ executable: !readOnly })
      : '&:focus { outline: none; }' // it's important that we set outline to none so the default outline doesn't show.
}
  `
}
